<template>
  <div>
    <div class="page-card">
      <h2 class="page-title">自动配菜</h2>
      <el-form label-width="110px" @submit.native.prevent="onGenerate">
        <el-row :gutter="20">
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item label="订餐日期">
              <el-date-picker
                v-model="form.orderDate"
                type="date"
                placeholder="请选择"
                value-format="yyyy-MM-dd"
                style="width: 100%;"
              />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item label="包厢名">
              <el-select v-model="form.roomName" placeholder="请选择" clearable style="width: 100%;">
                <el-option
                  v-for="name in roomOptions"
                  :key="name"
                  :label="name"
                  :value="name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item label="顾客人数">
              <el-input v-model="form.guestCount" placeholder="请输入" type="number" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item label="人均餐标">
              <el-input v-model="form.perCapita" placeholder="请输入" type="number" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item label="目标利润率">
              <el-input v-model="form.profitRatePercent" placeholder="百分比" type="number">
                <template slot="append">%</template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item label="是否需要煨菜">
              <el-radio-group v-model="form.needWei">
                <el-radio :label="false">不需要</el-radio>
                <el-radio :label="true">需要</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        <div class="form-actions">
          <el-button type="primary" native-type="submit">一键生成菜单</el-button>
        </div>
      </el-form>
    </div>

    <div v-if="result && result.ok" class="page-card">
      <div class="section-head">
        <h2 class="page-title" style="margin-bottom: 0;">汇总</h2>
      </div>
      <div class="summary-row">
        <div class="summary-item">总预算：<strong>{{ formatMoney(result.totalBudget) }}</strong></div>
        <div class="summary-item">总售价：<strong>{{ formatMoney(result.totalPrice) }}</strong></div>
        <div class="summary-item">总成本：<strong>{{ formatMoney(result.totalCost) }}</strong></div>
        <div class="summary-item">预估利润率：<strong>{{ formatRate(result.estimatedProfitRate) }}</strong></div>
      </div>
    </div>

    <div v-if="result && result.ok" class="page-card">
      <div class="section-head">
        <div class="section-head-left">
          <h2 class="page-title" style="margin-bottom: 0;">推荐菜单</h2>
          <el-cascader
            v-model="addDishPath"
            :options="dishCascaderOptions"
            :props="addCascaderProps"
            filterable
            clearable
            collapse-tags
            placeholder="搜索或按分类多选新增"
            style="width: 320px;"
            @visible-change="onAddPanelVisible"
          />
          <el-button
            size="small"
            type="primary"
            plain
            :disabled="!hasAddSelection"
            @click="addSelectedDishes"
          >添加</el-button>
        </div>
        <div class="menu-toolbar">
          <el-button
            v-if="pdfReady"
            :loading="pdfBusy"
            @click="downloadMenu"
          >下载菜单</el-button>
          <el-button
            type="primary"
            :loading="pdfBusy"
            :disabled="!result.dishes.length"
            @click="confirmMenu"
          >确认</el-button>
        </div>
      </div>
      <el-table
        :data="result.dishes"
        border
        stripe
        highlight-current-row
        empty-text="未能选出任何菜品"
        style="width: 100%;"
      >
        <el-table-column label="分类" width="120" align="center">
          <template slot-scope="scope">{{ categoryLabel(scope.row.category) }}</template>
        </el-table-column>
        <el-table-column prop="name" label="菜品名称" min-width="140" />
        <el-table-column prop="qty" label="人数/份数" width="100" align="center" />
        <el-table-column label="单人份售价" width="120" align="center">
          <template slot-scope="scope">{{ formatMoney(scope.row.price) }}</template>
        </el-table-column>
        <el-table-column label="单人份成本" width="120" align="center">
          <template slot-scope="scope">{{ formatMoney(scope.row.cost) }}</template>
        </el-table-column>
        <el-table-column label="菜品售价" width="120" align="center">
          <template slot-scope="scope">{{ formatMoney(scope.row.linePrice) }}</template>
        </el-table-column>
        <el-table-column label="菜品成本" width="120" align="center">
          <template slot-scope="scope">{{ formatMoney(scope.row.lineCost) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="90" align="center">
          <template slot-scope="scope">
            <el-button type="text" @click="removeMenuDish(scope.$index)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import { categoryLabel } from '@/constants/categories'
import { loadDishes } from '@/utils/storage'
import { generateMenu } from '@/utils/menuGenerator'
import { buildMenuResult, calcEstimatedProfitRate, estimatedRateIssue, isPriceOverBudget, toMenuLine } from '@/utils/menuBudget'
import { DISH_CASCADER_PROPS, buildDishCascaderOptions } from '@/utils/dishCascader'
import { takePendingMenu } from '@/utils/pendingMenu'
import { loadGenerateForm, saveGenerateForm } from '@/utils/generateFormStore'
import { addMenuHistory } from '@/utils/menuHistory'
import { downloadBlob } from '@/utils/menuPdf'
import { historyRecordToPdfBlob } from '@/utils/textPdf'
import { buildHistoryFileStem } from '@/utils/historyFileName'

export default {
  name: 'MenuGenerate',
  data() {
    return {
      roomOptions: ['玉娇轩', '笼雾轩', '临风轩', '听雨', '承露'],
      form: {
        orderDate: '',
        roomName: '',
        guestCount: '',
        perCapita: '',
        profitRatePercent: '',
        needWei: false
      },
      result: null,
      pdfReady: false,
      pdfBusy: false,
      pdfBlob: null,
      pdfFileName: '',
      allDishes: [],
      addDishPath: [],
      addCascaderProps: {
        ...DISH_CASCADER_PROPS,
        multiple: true
      },
      syncingForm: false
    }
  },
  computed: {
    dishNames() {
      if (!this.result || !this.result.dishes) return []
      return this.result.dishes.map((d) => d.name)
    },
    dishCascaderOptions() {
      return buildDishCascaderOptions(this.allDishes)
    },
    hasAddSelection() {
      return (this.addDishPath || []).some((path) => Array.isArray(path) && path.length >= 2)
    }
  },
  watch: {
    'form.guestCount'() {
      this.onFormChange()
    },
    'form.perCapita'() {
      this.onFormChange()
    },
    'form.profitRatePercent'() {
      this.onFormChange()
    },
    'form.orderDate'() {
      saveGenerateForm(this.form)
    },
    'form.roomName'() {
      saveGenerateForm(this.form)
    },
    'form.needWei'() {
      saveGenerateForm(this.form)
    }
  },
  created() {
    this.restoreGenerateForm()
    this.refreshDishPool()
    this.consumePendingMenu()
  },
  activated() {
    this.refreshDishPool()
    this.consumePendingMenu()
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.consumePendingMenu()
    })
  },
  methods: {
    categoryLabel,
    formatMoney(n) {
      return Number(n).toFixed(2)
    },
    formatRate(rate) {
      if (rate === null || rate === undefined) return '-'
      return `${(rate * 100).toFixed(2)}%`
    },
    resetPdf() {
      this.pdfReady = false
      this.pdfBusy = false
      this.pdfBlob = null
      this.pdfFileName = ''
    },
    refreshDishPool() {
      this.allDishes = loadDishes()
    },
    restoreGenerateForm() {
      const saved = loadGenerateForm()
      if (!saved) return
      this.syncingForm = true
      Object.assign(this.form, saved)
      this.$nextTick(() => {
        this.syncingForm = false
      })
    },
    onFormChange() {
      saveGenerateForm(this.form)
      if (this.syncingForm) return
      this.rebuildMenuLines()
    },
    consumePendingMenu() {
      const pending = takePendingMenu()
      if (!pending) return
      const dishes = pending.dishes || []
      const form = pending.form
      if (!dishes.length && !form) return
      if (form) {
        this.syncingForm = true
        Object.assign(this.form, form)
        saveGenerateForm(this.form)
        this.$nextTick(() => {
          this.syncingForm = false
        })
      }
      if (dishes.length) {
        this.applyDishesAsMenu(dishes)
        this.$message.success(`已带入 ${dishes.length} 道菜及配菜信息`)
      } else {
        this.$message.success('已带入历史菜单的人数、餐标等信息')
      }
    },
    sourceDishesFromResult() {
      if (!this.result || !this.result.dishes) return []
      return this.result.dishes.map((d) => ({
        id: d.id,
        name: d.name,
        price: d.price,
        cost: d.cost,
        category: d.category
      }))
    },
    applyDishesAsMenu(sourceDishes) {
      this.resetPdf()
      this.result = buildMenuResult(sourceDishes, this.form)
      this.warnIfBelowTarget()
    },
    rebuildMenuLines() {
      if (!this.result || !this.result.ok) return
      this.result = buildMenuResult(this.sourceDishesFromResult(), this.form)
      if (isPriceOverBudget(this.result.totalPrice, this.result.totalBudget)) {
        this.$message.warning('当前菜单总售价高于总预算，请调整菜品或餐标')
      }
    },
    warnIfBelowTarget() {
      if (!this.result || !this.result.ok) return
      if (isPriceOverBudget(this.result.totalPrice, this.result.totalBudget)) {
        this.$message.warning('当前菜单总售价高于总预算，请调整菜品或餐标')
        return
      }
      const issue = estimatedRateIssue(this.result.estimatedProfitRate, this.form.profitRatePercent)
      if (issue) this.$message.warning(`当前菜单${issue}，请调整菜品或目标利润率`)
    },
    /**
     * 按当前菜单行重算总售价、总成本、预估利润率
     */
    recalcSummary() {
      if (!this.result) return
      const totalPrice = this.result.dishes.reduce((sum, d) => sum + Number(d.linePrice), 0)
      const totalCost = this.result.dishes.reduce((sum, d) => sum + Number(d.lineCost), 0)
      this.$set(this.result, 'totalPrice', totalPrice)
      this.$set(this.result, 'totalCost', totalCost)
      this.$set(
        this.result,
        'estimatedProfitRate',
        calcEstimatedProfitRate(totalPrice, totalCost)
      )
    },
    onAddPanelVisible(visible) {
      if (visible) return
      if (this.hasAddSelection) this.addSelectedDishes()
    },
    addSelectedDishes() {
      const paths = this.addDishPath || []
      const ids = []
      paths.forEach((path) => {
        if (!Array.isArray(path) || path.length < 2) return
        const id = path[1]
        if (id && ids.indexOf(id) < 0) ids.push(id)
      })
      this.addDishPath = []
      if (!ids.length) return
      if (!this.result || !this.result.ok) return

      const picked = []
      const skippedExist = []
      ids.forEach((id) => {
        const dish = this.allDishes.find((d) => d.id === id)
        if (!dish) return
        const exists = this.result.dishes.some((d) => d.id === dish.id)
          || picked.some((d) => d.id === dish.id)
        if (exists) {
          skippedExist.push(dish.name)
          return
        }
        picked.push(dish)
      })
      if (!picked.length) {
        if (skippedExist.length) this.$message.warning('所选菜品已在菜单中')
        return
      }

      const guestCount = Number(this.form.guestCount) || 1
      const lines = picked.map((dish) => toMenuLine(dish, guestCount))
      const extraPrice = lines.reduce((sum, d) => sum + Number(d.linePrice), 0)
      const extraCost = lines.reduce((sum, d) => sum + Number(d.lineCost), 0)
      const nextPrice = this.result.dishes.reduce((sum, d) => sum + Number(d.linePrice), 0) + extraPrice
      const nextCost = this.result.dishes.reduce((sum, d) => sum + Number(d.lineCost), 0) + extraCost
      if (isPriceOverBudget(nextPrice, this.result.totalBudget)) {
        this.$message.warning('加入所选菜后总售价将高于总预算，已取消添加')
        return
      }
      const estimated = calcEstimatedProfitRate(nextPrice, nextCost)
      const addIssue = estimatedRateIssue(estimated, this.form.profitRatePercent)
      if (addIssue) {
        this.$message.warning(`加入所选菜后${addIssue}，已取消添加`)
        return
      }

      lines.forEach((line) => this.result.dishes.push(line))
      this.resetPdf()
      this.recalcSummary()
      const names = picked.map((d) => `「${d.name}」`).join('、')
      this.$message.success(`已添加 ${names}`)
    },
    removeMenuDish(index) {
      const row = this.result.dishes[index]
      const nextDishes = this.result.dishes.filter((_, i) => i !== index)
      const nextPrice = nextDishes.reduce((sum, d) => sum + Number(d.linePrice), 0)
      const nextCost = nextDishes.reduce((sum, d) => sum + Number(d.lineCost), 0)
      const estimated = calcEstimatedProfitRate(nextPrice, nextCost)
      const removeIssue = estimatedRateIssue(estimated, this.form.profitRatePercent)
      if (removeIssue) {
        this.$message.warning(`删除该菜后${removeIssue}，已取消删除`)
        return
      }
      this.result.dishes.splice(index, 1)
      this.resetPdf()
      this.recalcSummary()
      if (row) this.$message.success(`已删除「${row.name}」`)
    },
    validate() {
      const dishes = loadDishes()
      if (!dishes.length) {
        this.$message.warning('菜品池为空，请先到「菜谱管理」录入菜品')
        return null
      }
      const guestCount = Number(this.form.guestCount)
      if (!Number.isInteger(guestCount) || guestCount < 1) {
        this.$message.warning('顾客人数须为正整数')
        return null
      }
      const perCapita = Number(this.form.perCapita)
      if (!Number.isFinite(perCapita) || perCapita <= 0) {
        this.$message.warning('人均餐标须大于 0')
        return null
      }
      const rateRaw = this.form.profitRatePercent
      if (rateRaw !== '' && rateRaw != null) {
        const profitRatePercent = Number(rateRaw)
        if (!Number.isFinite(profitRatePercent) || profitRatePercent < 0 || profitRatePercent > 100) {
          this.$message.warning('目标利润率须在 0 到 100 之间')
          return null
        }
      }
      return {
        dishes,
        guestCount,
        perCapita,
        profitRatePercent: rateRaw === '' || rateRaw == null ? '' : Number(rateRaw),
        needWei: this.form.needWei === true
      }
    },
    onGenerate() {
      this.result = null
      this.resetPdf()
      const payload = this.validate()
      if (!payload) return
      this.refreshDishPool()
      const result = generateMenu(payload.dishes, payload)
      if (!result.ok) {
        this.$message.error(result.error)
        return
      }
      if (result.warnings && result.warnings.length) {
        result.warnings.forEach((tip) => this.$message.info(tip))
      }
      this.result = result
    },
    async confirmMenu() {
      if (!this.form.orderDate) {
        this.$message.warning('请填写订餐日期后再确认')
        return
      }
      if (!this.form.roomName) {
        this.$message.warning('请选择包厢名后再确认')
        return
      }
      if (!this.result || !this.result.dishes.length) {
        this.$message.warning('没有可确认的菜品')
        return
      }
      if (isPriceOverBudget(this.result.totalPrice, this.result.totalBudget)) {
        this.$message.warning('总售价高于总预算，无法确认菜单')
        return
      }
      const confirmIssue = estimatedRateIssue(this.result.estimatedProfitRate, this.form.profitRatePercent)
      if (confirmIssue) {
        this.$message.warning(`${confirmIssue}，无法确认菜单`)
        return
      }
      this.pdfBusy = true
      try {
        this.pdfBlob = await historyRecordToPdfBlob({
          title: `${this.form.orderDate} ${this.form.roomName}`.trim(),
          dishNames: this.dishNames
        })
        this.pdfFileName = `${buildHistoryFileStem(this.form.perCapita, this.form.guestCount)}.pdf`
        this.pdfReady = true
        addMenuHistory({
          orderDate: this.form.orderDate,
          roomName: this.form.roomName,
          perCapita: this.form.perCapita,
          guestCount: this.form.guestCount,
          profitRatePercent: this.form.profitRatePercent,
          needWei: this.form.needWei,
          dishNames: this.dishNames,
          dishes: this.sourceDishesFromResult()
        })
        this.$message.success('菜单已确认，可下载 PDF')
      } catch (e) {
        this.resetPdf()
        this.$message.error('生成 PDF 失败，请重试')
      } finally {
        this.pdfBusy = false
      }
    },
    downloadMenu() {
      if (!this.pdfBlob) return
      downloadBlob(this.pdfBlob, this.pdfFileName)
    }
  }
}
</script>
